import React from 'react'
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import DatePicker from "react-datepicker";
import { Calendar, Search, FileText } from 'lucide-react';
import "react-datepicker/dist/react-datepicker.css";
import { showNotification } from "../../redux/slices/notificationSlice";
import {
  Chip,
  TextField,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  TablePagination
} from '@mui/material';
import PageHeader from '../../componentsII/PageHeader';
import DocumentCard from '../../components/Scope3DataSubmissionCompany/DocumentCard';
import api from '../../axios/api';
import { useParams } from "react-router-dom";
import dayjs from 'dayjs';

const Scope3DataSubmissionCompany = () => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchTerm, setSearchTerm] = useState('');
  const [files, setFiles] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('All');
  const { company_id } = useParams();
  const [companyName, setCompanyName] = useState('');
  const [companyDataYears, setCompanyDataYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('All');

  // Avoid the "Cannot access 'dateRange' before initialization" error
  // by not destructuring it in the declaration line
  const startDate = dateRange[0];
  const endDate = dateRange[1];
  

  const fetchData = async () => {
    setLoading(true);
    try {
      // Prepare date parameters
      const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-01') : null;
      const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-01') : null;

      // Construct query parameters
      const params = new URLSearchParams();
      params.append('company_id', company_id);
      params.append('page', page.toString());
      params.append('limit', rowsPerPage.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (formattedStartDate) {
        params.append('startDate', formattedStartDate);
      }

      if (formattedEndDate) {
        params.append('endDate', formattedEndDate);
      }

      if (status && status !== 'All') {
        params.append('status', status);
      }

      if (selectedYear && selectedYear !== 'All') {
        params.append('year', selectedYear);
      }

      const response = await api.get(`/admin/scope3userData?${params.toString()}`);
      setFiles(response.data.data);
      setCompanyDataYears(response.data.dataYears);
      setCompanyName(response.data.dataYears[0].company_full_name)
      setTotalFiles(response.data.total || response.data.data.length);

      // Fetch company name if not already set
      if (!companyName && response.data.data.length > 0 && response.data.data[0].company_name) {
        setCompanyName(response.data.data[0].company_name);
      }
    } catch (error) {
      console.error("Error fetching scope 3 data:", error);
      dispatch(showNotification({
        message: "Failed to fetch data. Please try again.",
        type: "error"
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (update) => {
    setDateRange(update);
    setPage(0); // Reset to first page when filter changes
  };

  const clearDateRange = () => {
    setDateRange([null, null]);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Debounce search to avoid too many requests
    const timeoutId = setTimeout(() => {
      setPage(0); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    setStatus(value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleYearChange = (e) => {
    const value = e.target.value;
    setSelectedYear(value);
    setPage(0); // Reset to first page when year changes
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  // Fetch data when filters or pagination changes
  useEffect(() => {
    if (company_id) {
      fetchData();
    }
  }, [company_id, page, rowsPerPage, searchTerm, dateRange, status, selectedYear]);

  return (
    <div>
      <PageHeader title={`${companyName || 'Company Files'}`}>
        <div className="flex justify-between items-center gap-5 mt-5 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search by filename..."
              value={searchTerm}
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />
              }}
              onChange={handleSearch}
              className="min-w-[250px]"
            />

            {/* DatePicker */}
            <div className="relative flex items-center">
              <Calendar className="absolute left-3 text-gray-500 w-4 h-4" />
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                dateFormat="MMM yyyy"
                showMonthYearPicker
                placeholderText="Filter by month/year"
                className="pl-9 py-2 border border-gray-300 rounded-md w-full min-w-[250px] text-sm focus:outline-none"
                isClearable={false}
              />
              {(startDate || endDate) && (
                <div className="ml-2">
                  <Chip
                    label={`${startDate ? format(startDate, 'dd MMM yyyy') : ''} ${endDate ? '- ' + format(endDate, 'dd MMM yyyy') : ''}`}
                    onDelete={clearDateRange}
                    size="small"
                  />
                </div>
              )}
            </div>

            {/* Status Filter */}
            <FormControl size="small" className="w-[200px]">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={status}
                label="Status"
                onChange={handleStatusChange}
              >
                <MenuItem value="All">All Status</MenuItem>

                <MenuItem value="data_unavailable">Data Unavailable</MenuItem>
                <MenuItem value="unavailable_for_review">Unavailable For Review</MenuItem>
                <MenuItem value="pending">Available For Review</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="reviewed">Review Done</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" className="w-[200px]">
              <InputLabel id="data-years-filter-label">Reporting Years</InputLabel>
              <Select
                labelId="data-years-filter-label"
                value={selectedYear}
                label="Reporting Years"
                onChange={handleYearChange}
              >
                <MenuItem value="All">All</MenuItem>
                {companyDataYears.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {dayjs().month(year.start_year_month).format("MMM")} {year.start_year} - {dayjs().month(year.end_year_month).format("MMM")} {year.end_year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          </div>
        </div>
      </PageHeader>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <CircularProgress />
        </div>
      ) : files.length === 0 ? (
        <div className="flex flex-col justify-center items-center py-20 text-gray-500">
          <FileText className="w-16 h-16 mb-4" />
          <p className="text-lg">No files found</p>
          <p className="text-sm mt-2">Try adjusting your filters</p>
        </div>
      ) : (
        <>
          {/* Document Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
            {files.map((doc) => (
              <DocumentCard key={doc.id} document={doc} refetch={fetchData} />
            ))}
          </div>

          {/* Server-side Pagination with Items Per Page */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <TablePagination
              component="div"
              count={totalFiles}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Files per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
            />
          </Box>
        </>
      )}
    </div>
  );
};

export default Scope3DataSubmissionCompany;


const datePickerStyles = `
        .react-datepicker {
          font - family: "Roboto", "Helvetica", "Arial", sans-serif;
        font-size: 1rem;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

        .react-datepicker__month-container {
          float: none;
        width: 280px;
  }

        .react-datepicker__header {
          background - color: #f5f5f5;
        border-bottom: 1px solid #e0e0e0;
        padding: 1rem;
  }

        .react-datepicker__month-wrapper {
          display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.5rem;
        padding: 0.5rem;
  }

        .react-datepicker__month-text {
          margin: 0.5rem;
        padding: 0.5rem;
        width: auto !important;
        border-radius: 4px;
  }

        .react-datepicker__month-text:hover {
          background - color: #f0f0f0;
  }

        .react-datepicker__month-text--selected {
          background - color: #1976d2 !important;
        color: white !important;
  }

        .react-datepicker__year-wrapper {
          display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 0.5rem;
        padding: 0.5rem;
        max-width: none;
  }

        .react-datepicker__year-text {
          padding: 0.5rem;
        width: auto !important;
        border-radius: 4px;
  }

        .react-datepicker__year-text:hover {
          background - color: #f0f0f0;
  }

        .react-datepicker__year-text--selected {
          background - color: #1976d2 !important;
        color: white !important;
  }

        .date-picker-input {
          width: 100%;
        padding: 0.50rem;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        font-size: 1rem;
        background-color: white;
        cursor: pointer;
        transition: border-color 0.2s ease;
  }

        .date-picker-input:hover {
          border - color: #1976d2;
  }

        .date-picker-input:focus {
          border - color: #1976d2;
        outline: none;
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
  }

        .react-datepicker-popper {
          z - index: 9999 !important;
  }
        `;


